import React from 'react';
import PropTypes from 'prop-types';
import Spinner from './Spinner';
import './LoadingSystem.css';

/**
 * FullPageLoader - Indicador de carga que ocupa toda la pantalla u overlay
 * 
 * Casos de uso:
 * - Carga inicial de la aplicación o página
 * - Procesos que bloquean la interacción (generación de PDFs, procesamiento de datos)
 * - Transiciones entre páginas o vistas principales
 * 
 * Mejores prácticas:
 * - Usar solo cuando sea necesario bloquear toda la interfaz
 * - Siempre incluir un mensaje descriptivo del proceso
 * - Considerar usar un skeleton específico si el layout es predecible
 * - Para procesos largos, considerar agregar un indicador de progreso
 * 
 * @example
 * // Carga de página completa
 * {isLoading && (
 *   <FullPageLoader 
 *     message="Cargando datos..." 
 *     backdrop="blur"
 *   />
 * )}
 * 
 * @example
 * // Overlay modal durante procesamiento
 * {isProcessing && (
 *   <FullPageLoader 
 *     message="Generando reporte PDF..."
 *     backdrop="dark"
 *     spinnerSize="large"
 *   />
 * )}
 */
const FullPageLoader = ({ 
  message = 'Cargando...',
  submessage = '',
  backdrop = 'light',
  spinnerSize = 'large',
  spinnerColor = 'primary',
  logo = null,
  className = ''
}) => {
  const containerClass = `
    loading-fullpage
    loading-fullpage--backdrop-${backdrop}
    ${className}
  `.trim();

  return (
    <div className={containerClass} role="alert" aria-live="polite" aria-busy="true">
      <div className="loading-fullpage__content">
        {logo && (
          <div className="loading-fullpage__logo">
            {typeof logo === 'string' ? <img src={logo} alt="Logo" /> : logo}
          </div>
        )}
        
        <Spinner size={spinnerSize} color={spinnerColor} />
        
        {message && (
          <h2 className="loading-fullpage__message">{message}</h2>
        )}
        
        {submessage && (
          <p className="loading-fullpage__submessage">{submessage}</p>
        )}
      </div>
    </div>
  );
};

FullPageLoader.propTypes = {
  /** Mensaje principal que se muestra */
  message: PropTypes.string,
  /** Mensaje secundario opcional (más pequeño) */
  submessage: PropTypes.string,
  /** Tipo de fondo del overlay */
  backdrop: PropTypes.oneOf(['light', 'dark', 'blur', 'transparent']),
  /** Tamaño del spinner */
  spinnerSize: PropTypes.oneOf(['medium', 'large', 'xlarge']),
  /** Color del spinner */
  spinnerColor: PropTypes.oneOf(['primary', 'secondary', 'accent', 'white']),
  /** Logo opcional a mostrar encima del spinner (URL o componente) */
  logo: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Clase CSS adicional */
  className: PropTypes.string,
};

export default FullPageLoader;
